import {type FileDiff, type PcbDiff, type SchDiff, type SymlinkDiff, type SubmoduleDiff, type PreviewableTreeNode, type ProjectPageDiff, FILE_DIFF_STATUS, type TreeNode, type PdfDiff} from './types/file_tree.ts';

import type {ErrorPreview, FileDiffPreview, Preview, ProjectPageDiffPreview} from './types/previews.ts';
import {errorPreview, fileDiffPreview, loadingPreview, isFileDiff, isProjectPageDiff} from './types/previews.ts';

import {GET} from '../modules/fetch.ts';
import {SVG} from '../components/svg_utils.ts';
import {LRUCache} from 'lru-cache';
import type {DiffInfo} from './store.ts';
import {buildDiffPreviewRoute, buildPreviewRoute} from '../components/AllSpiceDiff/AllSpiceDiffViewRouteBuilder.ts';
import type {Container} from '../container.ts';
import {AllSpiceJsonLoader, SCHEMA_TYPES, type SchemaType} from '../features/allspicejson.ts';
import {LibraryComponent} from '../components/allspice_render/library_component.ts';
import {timing} from '../analytics.ts';
import type {ProjectData} from './types/project-data.ts';

const lruOptions = {
  max: 10,
  maxSize: 100_000_000,
  dispose(value: Preview | DiffMeta) {
    if ('cleanup' in value && typeof value.cleanup === 'function') {
      value.cleanup();
    }
  },
};

export type PreviewURLs = {
  fileDiff: string;
};

type DiffFile = FileDiff | PcbDiff | SchDiff | SymlinkDiff | SubmoduleDiff | PdfDiff;

type DiffMeta = {
  comments?: HTMLElement;
  variants?: Element;
};

// assignDiffMetaToPreview will assign correct aspects of a diff meta
// to parts of a preview
function assignDiffMetaToPreview(preview: Preview, meta: DiffMeta) {
  if (isProjectPageDiff(preview) || isFileDiff(preview)) {
    preview.commentHtml = meta.comments;
    preview.variantHtml = meta.variants;
  }
}

/**
 * Loader manages the caching and loading of previews.
 */

export class Loader {
  cache: LRUCache<string, Preview | DiffMeta>;
  container: Container;
  diffInfo: DiffInfo;
  previewURLs: PreviewURLs;

  constructor(previewURLs: PreviewURLs, container: Container, diffInfo: DiffInfo) {
    this.cache = new LRUCache(lruOptions);
    this.container = container;
    this.diffInfo = diffInfo;
    this.previewURLs = previewURLs;
  }

  private previewKey(file: PreviewableTreeNode): string {
    return `preview:${file.nameHash || file.headPath}`;
  }

  private diffMetaKey(file: PreviewableTreeNode): string {
    return `dm:${file.headPath}`;
  }

  /**
   * Load the preview of a file from the server
   */
  GetPreview(file: PreviewableTreeNode): Preview {
    // check to see if we have the preview cached
    const previewKey = this.previewKey(file);
    const metaKey = this.diffMetaKey(file);

    const cachedPreview = this.cache.get(previewKey) as Preview | undefined;

    const diffMeta = this.cache.get(metaKey) as DiffMeta | undefined;
    if (cachedPreview !== undefined && diffMeta !== undefined) {
      assignDiffMetaToPreview(cachedPreview, diffMeta);
    }

    if (cachedPreview !== undefined) {
      return cachedPreview;
    }

    // Start loading the preview from the server and store a reference to the promise.
    //
    // Note on concurrency: while is may seem like a race condition between adding the promise
    // to activeRequests and the promise resolving and removing itself from the active requests,
    // calling an async function runs until it hits an await, at which point it enqueues a
    // microtask which will only start running after the current JS stack is empty.
    //
    // See: https://developer.mozilla.org/en-US/docs/Web/API/HTML_DOM_API/Microtask_guide
    const previewPromise = (async () => {
      // note this function cannot throw an error instead all errors must be converted into a PreviewError
      const previewFetch = fetchPreview(file, this.previewURLs, this.container, this.diffInfo);

      let metaFetch: Promise<[DiffMeta | null, number]> = Promise.resolve([null, 0]);
      if (!diffMeta) {
        metaFetch = fetchMeta(file as ProjectPageDiff, this.previewURLs.fileDiff);
      }

      const [[preview, estPreviewSizeBytes], [diffMetaNew, estCommentSizeBytes]] = await Promise.all([
        previewFetch,
        metaFetch,
      ]);

      // cache the preview
      this.cache.set(previewKey, preview, {size: estPreviewSizeBytes});

      if (diffMetaNew) {
        // cache the comment section
        this.cache.set(metaKey, diffMetaNew, {size: estCommentSizeBytes});
        assignDiffMetaToPreview(preview, diffMetaNew);
      }

      // return the preview
      return preview;
    })();

    const preview = loadingPreview(file.headPath, previewPromise);
    this.cache.set(previewKey, preview, {size: 1});

    return preview;
  }
}

/**
 * Fetch a preview from the server
 */
async function fetchPreview(
  file: TreeNode,
  fetchURLs: PreviewURLs,
  container: Container,
  diffInfo: DiffInfo,
): Promise<[Preview, number]> {
  // Note this function cannot throw an error, instead it must return a ErrorPreview which is displayed to the user
  switch (file.ty) {
    // ECAD rendering
    case 'sch-diff':
    case 'pcb-diff':
    case 'schlib-diff':
    case 'pcblib-diff':
    case 'proj-diff':
    case 'multi-page-diff':
      return await fetchProjectDiffPreview(file as DiffFile, fetchURLs.fileDiff);
    // Render a single page of an ECAD file
    case 'proj-page-diff':
      return await fetchProjectPageDiffPreview(file, container, diffInfo);
    // Fallback to file-based rendering
    case 'pdf-diff':
    case 'symlink-diff':
    case 'submodule-diff':
    case 'file-diff':
      return await fetchFileDiffPreview(file as DiffFile, fetchURLs.fileDiff);
    default:
      return [errorPreview(file.headPath, `Preview not implemented for ${file.ty}`), 1];
  }
}

/**
 * Parse the variant dropdown from the diff document
 */
function parseVariantDropdown(document: Document, file: DiffFile | ProjectPageDiff): Element | null {
  const variantDropdown = document.querySelector('variant-dropdown');
  if (variantDropdown) {
    variantDropdown.setAttribute('head-tree-path', file.headPath);
  }

  return variantDropdown;
}

/**
 * Fetch the diff document from the server
 */
async function fetchDiffDocument(file: DiffFile | ProjectPageDiff, fetchFileDiffURL: string): Promise<[Document, number]> {
  // construct the URL
  const url = new URL(fetchFileDiffURL);
  url.searchParams.append('file-only', 'true');
  const requestedFiles = new Set([file.headPath, file.basePath].filter(Boolean));
  for (const requestedFile of requestedFiles) {
    url.searchParams.append('files', requestedFile);
  }

  // make the request
  const resp = await GET(url.toString());

  // check for errors
  if (!resp.ok) {
    throw new Error(`Failed to fetch preview: ${resp.statusText}`);
  }

  // parse the response
  const text = await resp.text();
  const parser = new DOMParser();

  // extract the portion of the HTML response we want
  const document = parser.parseFromString(text, 'text/html');
  return [document, text.length * 2]; // assume 2 bytes per character (worst case)
}

async function fetchMeta(file: DiffFile | ProjectPageDiff, fetchFileDiffURL: string): Promise<[DiffMeta | null, number]> {
  try {
    const [document] = await fetchDiffDocument(file, fetchFileDiffURL);

    const commentHtml = document.querySelector('.code-conversations') as HTMLElement | null;
    if (commentHtml) {
      for (const snippetEl of commentHtml.querySelectorAll('allspice-diff-snippet')) {
        snippetEl.setAttribute(':lazy-load', 'false');
      }
    }

    const commentSize = commentHtml?.outerHTML.length ?
      commentHtml.outerHTML.length * 2 :
      1;

    const variantHtml = parseVariantDropdown(document, file);
    const variantSize = variantHtml?.outerHTML.length ?
      variantHtml.outerHTML.length * 2 :
      1;

    return [{comments: commentHtml, variants: variantHtml}, commentSize + variantSize];
  } catch (e) {
    console.error('Failed to fetch comments', e);
    return [null, 1];
  }
}

async function fetchProjectPageDiffPreview(file: ProjectPageDiff, container: Container, diffInfo: DiffInfo): Promise<[ProjectPageDiffPreview | ErrorPreview, number]> {
  if (file.status === FILE_DIFF_STATUS.DELETED) {
    return [{ty: 'proj-page-diff', file, pageComponent: null, isDiffLoadedPromise: null, projectData: null}, 2000];
  }

  const startTime = performance.now();
  const diffFromPreviewUrl = buildDiffPreviewRoute(
    container,
    diffInfo.username,
    diffInfo.reponame,
    'from',
    diffInfo.headCommit,
    file.headPath,
    diffInfo.username,
    diffInfo.reponame,
    diffInfo.baseCommit,
    file.basePath,
  );

  const diffToPreviewUrl = buildDiffPreviewRoute(
    container,
    diffInfo.username,
    diffInfo.reponame,
    'to',
    diffInfo.headCommit,
    file.headPath,
    diffInfo.username,
    diffInfo.reponame,
    diffInfo.baseCommit,
    file.status === FILE_DIFF_STATUS.ADDED ? '' : file.basePath,
  );

  const headSvgUrl = buildPreviewRoute(
    container,
    diffInfo.username,
    diffInfo.reponame,
    diffInfo.headCommit,
    file.headPath,
  );

  let baseUrl = AllSpiceJsonLoader.buildUrl(container,
    diffInfo.username, diffInfo.reponame, diffInfo.baseCommit, file.basePath);
  const headUrl = AllSpiceJsonLoader.buildUrl(container,
    diffInfo.username, diffInfo.reponame, diffInfo.headCommit, file.headPath);

  if (file.status === FILE_DIFF_STATUS.ADDED) {
    baseUrl = null;
  }

  const fetchAllspiceJson = AllSpiceJsonLoader.load(
    container,
    headUrl,
    baseUrl,
  );

  // Also load project-level data for variant mappings
  const fetchProjectJson = AllSpiceJsonLoader.load<ProjectData>(
    container,
    AllSpiceJsonLoader.buildPrjRangeUrl(container, diffInfo.username, diffInfo.reponame, diffInfo.baseCommit, diffInfo.headCommit),
    null,
  );

  const fetchSvg = async (url: string): Promise<[SVGSVGElement, number]> => {
    const svg = await SVG.uncachedFetch(container, `${url}&page_id=${file.pageId}`);

    // Use any provided blob sizes. Otherwise default to a hardcoded estimate of 100k.
    const estimatedSize = svg._blob ? svg._blob.size * 2 : 100 * 1024;
    const el = await svg.svg();

    return [el, estimatedSize];
  };

  try {
    const [allspiceJson, projectJson, [headSvgEl, headSvgSize], [diffToSvgEl, diffToSvgSize], maybeFromResult] = await Promise.all([
      fetchAllspiceJson,
      fetchProjectJson,
      fetchSvg(headSvgUrl),
      fetchSvg(diffToPreviewUrl),
      (file.status !== FILE_DIFF_STATUS.ADDED) ? fetchSvg(diffFromPreviewUrl) : Promise.resolve(undefined),
    ]);

    const [diffFromSvgEl, diffFromSvgSize] = maybeFromResult || [null, null];

    // Pages from Crysknife JSON have at least id/name/type, matching RawComponent shape
    const pages = (allspiceJson.headJson?.pages || []) as {id: string; name: string; type?: SchemaType; [key: string]: unknown}[];
    const basePages = (allspiceJson.baseJson?.pages || []) as {id: string; name: string; type?: SchemaType; [key: string]: unknown}[];
    const headJson = pages.find(({id}) => id === file.pageId);
    const baseJson = basePages.find(({id}) => id === file.basePageId);

    if (headJson) {
      headJson.type = SCHEMA_TYPES.SINGLE_PAGE_SCHEMATIC;
    }
    if (baseJson) {
      baseJson.type = SCHEMA_TYPES.SINGLE_PAGE_SCHEMATIC;
    }

    const estPreviewSizeBytes = headSvgSize + diffToSvgSize + (diffFromSvgSize || 0);
    const pageComponent = new LibraryComponent(true, headJson, baseJson, file.status === FILE_DIFF_STATUS.ADDED, false, headSvgEl, diffToSvgEl, diffFromSvgEl);
    const nets = (allspiceJson.headJson?.nets ?? allspiceJson.baseJson?.nets ?? undefined) as Record<string, {id: string; name: string}> | undefined;
    timing('fetch_project_pages_diff_preview', performance.now() - startTime);

    return [{ty: 'proj-page-diff', file, pageComponent, isDiffLoadedPromise: Promise.resolve([null, null]), projectData: projectJson.headJson, nets}, estPreviewSizeBytes];
  } catch (e) {
    console.error('Failed to fetch page preview', e);
    return [errorPreview(file.headPath, 'Failed to fetch page preview'), 1];
  }
}

async function fetchFileDiffPreview(file: DiffFile, fetchFileDiffURL: string): Promise<[FileDiffPreview | ErrorPreview, number]> {
  let doc: Document;
  let estSizeBytes = 0;

  try {
    [doc, estSizeBytes] = await fetchDiffDocument(file, fetchFileDiffURL);
  } catch (e) {
    return [errorPreview(file.headPath, e.message), 300];
  }

  const diffElement = doc.querySelector('#diff-file-boxes')?.firstElementChild;
  if (!diffElement) {
    console.error('Could not find diff element in response');
    return [errorPreview(file.headPath, 'Failed to parse preview'), 1];
  }

  const variantDropdown = parseVariantDropdown(doc, file);

  // ensure the diff is unfolded
  diffElement.setAttribute('data-folded', 'false');

  let warnings = [];
  // extract warnings html from the response
  const warningsElem = doc.querySelector('#allspice-file-warnings');
  if (warningsElem) {
    try {
      const json = warningsElem.getAttribute(':sync-warnings');
      const parsed = JSON.parse(json);
      warnings = parsed;
    } catch (e) {
      console.error('Failed to parse warnings from `sync-warnings` attribute on element', e, warningsElem);
    }
  }

  return [fileDiffPreview(file.headPath, diffElement, variantDropdown, warnings), estSizeBytes];
}

async function fetchProjectDiffPreview(file: DiffFile, fetchFileDiffURL: string): Promise<[FileDiffPreview | ErrorPreview, number]> {
  let doc: Document;
  let estSizeBytes = 0;

  try {
    // Only load the head
    [doc, estSizeBytes] = await fetchDiffDocument({...file, basePath: file.headPath}, fetchFileDiffURL);
  } catch (e) {
    return [errorPreview(file.headPath, e.message), 300];
  }

  const diffElement = doc.querySelector('#diff-file-boxes')?.firstElementChild;
  if (!diffElement) {
    console.error('Could not find diff element in response');
    return [errorPreview(file.headPath, 'Failed to parse preview'), 1];
  }

  const variantDropdown = parseVariantDropdown(doc, file);

  // ensure the diff is unfolded
  diffElement.setAttribute('data-folded', 'false');

  if (file.status !== FILE_DIFF_STATUS.ADDED) {
    const rendered = diffElement.querySelector('allspice-diff');
    if (rendered) {
      rendered.setAttribute('base-tree-path', file.basePath);
    }
  }

  // reconstruct the renamed file name
  if (file.basePath !== file.headPath) {
    const fileDisplay = `${file.basePath} → ${file.headPath}`;
    const fileLink = diffElement.querySelector('.diff-file-name .file-link');
    if (fileLink) {
      fileLink.setAttribute('title', fileDisplay);
      fileLink.textContent = fileDisplay;
    }
    diffElement.setAttribute('data-base-path', file.basePath);
  }

  let warnings = [];
  // extract warnings html from the response
  const warningsElem = doc.querySelector('#allspice-file-warnings');
  if (warningsElem) {
    try {
      const json = warningsElem.getAttribute(':sync-warnings');
      const parsed = JSON.parse(json);
      warnings = parsed;
    } catch (e) {
      console.error('Failed to parse warnings from `sync-warnings` attribute on element', e, warningsElem);
    }
  }

  return [fileDiffPreview(file.headPath, diffElement, variantDropdown, warnings), estSizeBytes];
}
